import { useState, useEffect } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import { Loader2, CheckCircle, Repeat, Zap, Building2, Award, Crown, ArrowLeft, AlertTriangle } from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/lib/auth";

const targetIcons = { COACH: Award, PROVIDER: Building2, CONDO: Building2, LGU: Building2 };

export default function SubscriptionPlans() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [plans, setPlans] = useState([]);
  const [mySub, setMySub] = useState(null);
  const [loading, setLoading] = useState(true);
  const [subscribing, setSubscribing] = useState(null);
  const [paymentChecking, setPaymentChecking] = useState(false);

  useEffect(() => {
    Promise.all([
      api.get("/subscriptions/plans"),
      api.get("/subscriptions/my"),
    ]).then(([plansRes, subRes]) => {
      setPlans(plansRes.data);
      setMySub(subRes.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  // Poll payment status on return from Stripe
  useEffect(() => {
    const sessionId = searchParams.get("session_id");
    const subId = searchParams.get("sub_id");
    if (sessionId && subId) {
      setPaymentChecking(true);
      const poll = async (attempts = 0) => {
        if (attempts >= 6) { setPaymentChecking(false); return; }
        try {
          const res = await api.get(`/subscriptions/payment-status/${sessionId}`);
          if (res.data.payment_status === "paid") {
            toast.success("Subscription activated!");
            setPaymentChecking(false);
            const subRes = await api.get("/subscriptions/my");
            setMySub(subRes.data);
            return;
          }
        } catch {}
        setTimeout(() => poll(attempts + 1), 2000);
      };
      poll();
    }
  }, [searchParams]);

  const handleSubscribe = async (planId) => {
    if (!user) { navigate("/login"); return; }
    setSubscribing(planId);
    try {
      const originUrl = window.location.origin;
      const res = await api.post("/subscriptions/subscribe", { plan_id: planId, origin_url: originUrl });
      if (res.data.needs_payment && res.data.checkout_url) {
        window.location.href = res.data.checkout_url;
        return;
      }
      toast.success("Subscription activated!");
      setMySub(res.data.subscription);
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    setSubscribing(null);
  };

  const handleCancel = async () => {
    try {
      await api.post("/subscriptions/cancel");
      toast.success("Subscription cancelled");
      setMySub(null);
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  // Determine which plans to show based on user type
  const myTarget = user?.is_coach ? "COACH" : user?.role === "PROVIDER" ? "PROVIDER" : null;
  const filteredPlans = myTarget ? plans.filter(p => p.target === myTarget) : plans;

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-court-orange" /></div>;

  return (
    <div className="max-w-4xl mx-auto px-4 md:px-8 py-6 md:py-10" data-testid="subscription-plans-page">
      <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-4">
        <ArrowLeft className="w-4 h-4" />Back
      </button>

      <h1 className="font-heading text-3xl md:text-4xl font-bold uppercase tracking-tight flex items-center gap-3 mb-2">
        <Crown className="w-8 h-8 text-court-orange" />Subscription Plans
      </h1>
      <p className="text-muted-foreground mb-6">Choose a plan to unlock premium features</p>

      {paymentChecking && (
        <div className="bg-amber-50 border-2 border-amber-200 p-4 mb-6 flex items-center gap-3">
          <Loader2 className="w-5 h-5 animate-spin text-amber-500" />
          <p className="text-sm font-medium text-amber-800">Confirming your payment...</p>
        </div>
      )}

      {/* Current Subscription */}
      {mySub && (
        <div className="bg-white border-2 border-court-orange/30 p-5 mb-8" data-testid="current-subscription">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <p className="text-xs font-heading uppercase tracking-[0.15em] text-muted-foreground">Current Plan</p>
              <h3 className="font-heading text-xl font-bold uppercase">{mySub.plan_name}</h3>
              <p className="text-sm text-muted-foreground mt-1">₱{mySub.price?.toLocaleString()}/month - Active until {mySub.end_date}</p>
              {mySub.sessions_used !== undefined && (
                <p className="text-sm mt-1">Sessions: <span className="font-bold">{mySub.sessions_used}</span>/{mySub.plan_details?.session_limit === -1 ? "Unlimited" : mySub.plan_details?.session_limit} <span className="text-muted-foreground">({mySub.sessions_remaining} remaining)</span></p>
              )}
              {mySub.venues_used !== undefined && (
                <p className="text-sm mt-1">Venues: <span className="font-bold">{mySub.venues_used}</span>/{mySub.plan_details?.venue_limit === -1 ? "Unlimited" : mySub.plan_details?.venue_limit} <span className="text-muted-foreground">({mySub.venues_remaining} remaining)</span></p>
              )}
            </div>
            <div className="flex items-center gap-2">
              <Badge className="bg-green-100 text-green-800 text-xs font-heading uppercase"><CheckCircle className="w-3 h-3 mr-1 inline" />Active</Badge>
              <Button data-testid="cancel-sub-btn" size="sm" variant="outline" onClick={handleCancel}
                className="rounded-full border-2 border-red-200 text-red-500 hover:bg-red-50 text-xs font-heading uppercase">Cancel</Button>
            </div>
          </div>
        </div>
      )}

      {/* Plans Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="plans-grid">
        {filteredPlans.map(plan => {
          const isCurrentPlan = mySub?.plan_id === plan.plan_id;
          const Icon = targetIcons[plan.target] || Zap;
          return (
            <div key={plan.plan_id} className={`bg-white border-2 p-6 flex flex-col ${isCurrentPlan ? 'border-court-orange shadow-[3px_3px_0px_0px_rgba(234,88,12,0.3)]' : 'border-slate-200'}`}
              data-testid={`plan-${plan.plan_id}`}>
              <div className="flex items-center gap-2 mb-3">
                <Icon className="w-5 h-5 text-court-orange" />
                <h3 className="font-heading text-lg font-bold uppercase">{plan.name}</h3>
              </div>
              <p className="font-heading text-3xl font-bold mb-1">
                {plan.price > 0 ? <>₱{plan.price.toLocaleString()}<span className="text-sm font-normal text-muted-foreground">/mo</span></> : "FREE"}
              </p>
              <Separator className="my-3" />
              <ul className="space-y-2 mb-6 flex-1">
                {plan.features?.map((f, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm">
                    <CheckCircle className="w-4 h-4 text-lime-volt shrink-0 mt-0.5" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
              {isCurrentPlan ? (
                <Badge className="w-full justify-center py-2 bg-court-orange/10 text-court-orange font-heading uppercase">Current Plan</Badge>
              ) : (
                <Button data-testid={`subscribe-${plan.plan_id}`} onClick={() => handleSubscribe(plan.plan_id)}
                  disabled={subscribing === plan.plan_id}
                  className="w-full bg-court-orange hover:bg-court-orange-hover text-white rounded-full font-heading uppercase shadow-[2px_2px_0px_0px_rgba(0,0,0,1)]">
                  {subscribing === plan.plan_id ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  {plan.price > 0 ? `Subscribe - ₱${plan.price.toLocaleString()}` : "Get Started Free"}
                </Button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
